export const isSpotsAreUndefined = (max_spots) => {
    return [null, undefined, ''].includes(max_spots)
}

export const getSpotsAvailabilityInfos = (
    division,
    global_total_spots,
    competition_participants_count,
    divisionActiveReservationsCount,
    competitionActiveReservationsCount,
) => {
    const max_spots = isSpotsAreUndefined(global_total_spots) ? undefined : +global_total_spots

    const division_participants_count =
        typeof division.total_participants !== 'undefined' ? division.total_participants : division.participants?.length

    const rawDivisionSpots = division.ticketing?.division_spots
    // 🛑 0 = division not available (closed / no spots)
    const isDivisionDisabled =
        rawDivisionSpots !== null &&
        rawDivisionSpots !== undefined &&
        rawDivisionSpots !== '' &&
        Number(rawDivisionSpots) === 0

    if (isDivisionDisabled) {
        return {
            spots_available: 0,
            max_spots: 0,
            divisionDisabled: true,
        }
    }

    const division_max_spots = !isSpotsAreUndefined(rawDivisionSpots) ? Number(rawDivisionSpots) : undefined

    const isDivisionLimited = !isSpotsAreUndefined(rawDivisionSpots)

    const global_spots_available = max_spots - (competition_participants_count + competitionActiveReservationsCount)

    const division_spots_available =
        division_max_spots -
        ((isDivisionLimited ? division_participants_count : competition_participants_count) +
            divisionActiveReservationsCount)

    // if global max spots number is reached return 0 available spot
    if (max_spots && global_spots_available <= 0) {
        return {
            spots_available: 0,
            max_spots: division_max_spots || max_spots,
            possibleAvailableSpots: division_spots_available > 0 || !division_max_spots,
        }
    }

    // if division is unlimited
    if (!division_max_spots) {
        // if competition is unlimited
        if (!max_spots) {
            return {
                unlimited: true,
                max_spots: division_max_spots || max_spots,
            }
        }

        return {
            spots_available: max_spots - (competition_participants_count + competitionActiveReservationsCount),
            max_spots,
        }
    }

    // if competition is limited and division is limited
    if (max_spots && division_max_spots) {
        return {
            spots_available:
                division_spots_available > global_spots_available ? global_spots_available : division_spots_available,
            max_spots: max_spots > division_max_spots ? division_max_spots : max_spots,
        }
    }

    // if competition is unlimited and division is limited
    return {
        max_spots: division_max_spots,
        spots_available: division_spots_available,
    }
}

export const getDivisionListOptions = ({
    t,
    divisions = [],
    availabilityInfos = [],
    competition,
    competitionActiveReservationsCount = 0,
    competitionActiveReservationsEndTime,
    isQualif = false,
    disabled = false,
    selectedDivision,
    // Registration only: ReserveSpot serves the waiting list before the caller. Order edits
    // (UpdateOrderPopup) bypass the line server-side, so they leave it off.
    waitlistFirst = false,
}) => {
    const totalSpots = competition.ticketing.total_spots
    // Live booked counts from the availability endpoint; `total_participants` is frozen at page load
    // and turns paid holds into free spots on a long-open page. Fallback: older backend.
    const hasLiveCounts = availabilityInfos.some((o) => o.bookedCount !== undefined)
    const totalParticipants = hasLiveCounts
        ? availabilityInfos.reduce((sum, o) => sum + (o.bookedCount || 0), 0)
        : competition.total_participants
    const hideSpotsCount = competition.ticketing.hideSpotsCount

    return divisions
        .filter((division) => !division.unlisted)
        .sort((a, b) => a.position - b.position)
        .map((division) => {
            let available = true
            // proto: "4 Left / 50 Spots" caption, or a red "● Sold Out" dot when nothing is left
            let spotsLabel = ''
            let soldOut = false
            let waitInfo = ''
            // `left`: the live count behind spotsLabel (null when unlimited / full), for the trend arrow.
            let left = null
            // A full-but-not-closed capped division has a waiting list (mock `.is-full` vs `.is-closed`).
            let queueable = false
            let waitingCount = 0
            const isSelected = selectedDivision === division.name

            if (!isQualif) {
                const availabilityInfo = availabilityInfos.find((o) => o._division == division._id)
                // null = the organizer hides counts (hideSpotsCount): the line's length stays private too.
                waitingCount = hideSpotsCount ? null : availabilityInfo?.waitingCount || 0

                const { unlimited, spots_available: freeSpots, max_spots, possibleAvailableSpots, divisionDisabled } =
                    getSpotsAvailabilityInfos(
                        hasLiveCounts ? { ...division, total_participants: availabilityInfo?.bookedCount || 0 } : division,
                        totalSpots,
                        totalParticipants,
                        availabilityInfo?.divisionActiveReservationsCount || 0,
                        competitionActiveReservationsCount,
                    )
                // A freed spot belongs to the people in line (raw count: hideSpotsCount only hides the label).
                const inLine = waitlistFirst ? availabilityInfo?.waitingCount || 0 : 0
                const spots_available = inLine ? freeSpots - inLine : freeSpots

                // 0-spots division = not available. The athlete's own hold counts as an active
                // reservation, so the division they hold stays "available" for them even at 0 left —
                // never sold out, never queueable (they already have the spot).
                available = !divisionDisabled && (unlimited || spots_available > 0 || isSelected)

                if (divisionDisabled) {
                    soldOut = true
                } else if (unlimited) {
                    spotsLabel = ''
                } else if (spots_available > 0 || isSelected) {
                    if (!hideSpotsCount) {
                        left = Math.max(spots_available, 0)
                        spotsLabel = max_spots
                            ? t('{{left}} Left / {{total}} Spots', { left, total: max_spots })
                            : t('{{left}} Left', { left })
                    }
                } else {
                    soldOut = true
                    // Every spot (division or global cap) booked: the line is closed (backend isBookedOut,
                    // mock `.is-closed`). Holds still running → full, the line stays open.
                    const divisionSpots = Number(division.ticketing?.division_spots)
                    queueable = !(
                        (divisionSpots > 0 && (availabilityInfo?.bookedCount || 0) >= divisionSpots) ||
                        (hasLiveCounts && Number(totalSpots) > 0 && totalParticipants >= Number(totalSpots))
                    )

                    if (availabilityInfo?.divisionActiveReservationsCount > 0) {
                        waitInfo = t('A spot will be available in {{x}} mins', {
                            x: getReservationTimeLeft(
                                availabilityInfo.endTimeOfDivisionActiveReservationWithTheLessTimeLeft,
                            ),
                        })
                    }

                    if (competitionActiveReservationsCount > 0 && possibleAvailableSpots) {
                        waitInfo = t('A spot will be available in {{x}} mins', {
                            x: getReservationTimeLeft(competitionActiveReservationsEndTime),
                        })
                    }
                }
            }

            const isDisabled = disabled || !available
            const isTeam = division.type === 'team'
            const athletes = isTeam ? division.teamLength : 1
            // Same fallback as TicketingRegistrationCalculs.getApplicablePrice: divisions
            // without a specific price cost the competition default price.
            const price = division.ticketing?.division_price ?? competition.ticketing.default_price

            return {
                key: division._id,
                name: division.name,
                spotsLabel,
                soldOut,
                waitInfo,
                left,
                queueable,
                waitingCount,
                isDisabled,
                isSelected,
                kind: isTeam ? 'Team' : 'Indiv',
                athletes,
                price,
            }
        })
}

export const getReservationTimeLeft = (endTime) => {
    const now = Date.now()
    const timeleft = endTime - now
    const minutes = Math.floor((timeleft % (1000 * 60 * 60)) / (1000 * 60))
    return minutes
}
